<script lang="ts">
    import type { ColorVariation } from '@shared/types/types';

    interface Props {
        currentVariationId: string;
        detailPageLink: string;
        productLinkTitle: string;
        maxRenderedColors?: number;
        colors?: ColorVariation[];
        onClick?: (event: Event) => void;
    }

    let {
        currentVariationId,
        detailPageLink,
        productLinkTitle,
        maxRenderedColors = 5,
        colors = [],
        onClick,
    }: Props = $props();

    const totalNumberOfColors = $derived(colors?.length || 0);
    const surplusNumberOfColors = $derived(totalNumberOfColors - maxRenderedColors);
    const shownColors = $derived(colors.slice(0, maxRenderedColors));

    function handleKeyDown(event: KeyboardEvent) {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.preventDefault();
            onClick?.(event);
        }
    }
</script>

{#if totalNumberOfColors > 1}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <oc-interactive-overlay-v2
        class="reptile-color-thumbs"
        data-link-type="color_tiles"
        onclick={onClick}
        onkeydown={handleKeyDown}
        aria-label={productLinkTitle}
    >
        <div class="reptile-color-thumbs__link" data-masked-ref={btoa(detailPageLink)} role="link" tabindex={0}>
            {#each shownColors as [variationId, color] (variationId)}
                <span
                    aria-label={color.name}
                    class="reptile-color-thumbs__thumb"
                    data-color-name={color.name}
                    data-variation-id={variationId}
                >
                    <span
                        class={[
                            'reptile-color-thumbs__dot',
                            !color.hexCode ? color.baseColor || 'weiss' : '',
                            variationId === currentVariationId && 'reptile-color-thumbs__dot--selected',
                        ]}
                        style:background={color.hexCode ? `#${color.hexCode}` : undefined}
                    ></span>
                </span>
            {/each}
            {#if surplusNumberOfColors > 0}
                <span class="reptile-color-thumbs__surplus oc-headline-25">
                    +{surplusNumberOfColors}
                </span>
            {/if}
        </div>
    </oc-interactive-overlay-v2>
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/_mixins/breakpoint' as mixins;
    @use '@shared/styles/colors';

    .reptile-color-thumbs {
        display: block;

        &__link {
            display: flex;
            gap: tokens.$oc-semantic-spacing-25;
            margin: tokens.$oc-semantic-spacing-25 0;

            /*                                                                                                        */
            height: 100%;
            flex-flow: row nowrap;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-padding-left: tokens.$oc-semantic-spacing-25;
            align-items: center;
            cursor: pointer;
            text-decoration: none;
            color: inherit;

            @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
                flex-wrap: wrap;
            }
        }

        &__thumb {
            display: inline-flex;
            scroll-snap-align: start;
            scroll-snap-stop: always;
            margin: 0;
        }

        &__dot {
            display: block;
            height: tokens.$oc-base-dimension-16;
            width: tokens.$oc-base-dimension-16;
            border: tokens.$oc-semantic-border-width-100 solid tokens.$oc-semantic-color-disabled-border;
            border-radius: 50%;

            $base-colors: (
                'weiss': tokens.$oc-base-color-white-100,
                'schwarz': tokens.$oc-base-color-black-100,
                'braun': colors.$reptile-color-brown,
                'beige': colors.$reptile-color-beige,
                'gelb': colors.$reptile-color-yellow,
                'orange': colors.$reptile-color-orange,
                'rot': colors.$reptile-color-red,
                'rosa': colors.$reptile-color-pink,
                'lila': colors.$reptile-color-purple,
                'blau': colors.$reptile-color-blue,
                'gruen': colors.$reptile-color-green,
                'grün': colors.$reptile-color-green,
                'grau': colors.$reptile-color-gray,
            );

            @each $color-name, $color-value in $base-colors {
                &.#{$color-name} {
                    background: $color-value;
                }
            }

            &.goldfarben {
                background: linear-gradient(
                    to right top,
                    colors.$reptile-color-gold 0,
                    colors.$reptile-color-gold-dark 17%,
                    colors.$reptile-color-gold-light 54%,
                    colors.$reptile-color-gold-light 70%,
                    colors.$reptile-color-gold-dark 95%
                );
            }

            &.silberfarben {
                background: linear-gradient(
                    to right top,
                    colors.$reptile-color-silver-dark 17%,
                    colors.$reptile-color-silver-light 54%,
                    colors.$reptile-color-silver-light 70%,
                    colors.$reptile-color-silver-dark 95%
                );
            }

            &.bunt {
                background: linear-gradient(
                    to right top,
                    colors.$reptile-color-brown 0,
                    colors.$reptile-color-brown 10%,
                    colors.$reptile-color-pink 10%,
                    colors.$reptile-color-pink 23%,
                    colors.$reptile-color-purple 23%,
                    colors.$reptile-color-purple 37%,
                    colors.$reptile-color-green 37%,
                    colors.$reptile-color-green 48.5%,
                    colors.$reptile-color-blue 48.5%,
                    colors.$reptile-color-blue 63%,
                    colors.$reptile-color-yellow 63%,
                    colors.$reptile-color-yellow 76%,
                    colors.$reptile-color-orange 76%,
                    colors.$reptile-color-orange 90%,
                    colors.$reptile-color-red 90%,
                    colors.$reptile-color-red 100%
                );
            }

            &--selected {
                border-color: tokens.$oc-semantic-color-text-default;
                box-shadow: inset 0 0 0 2px tokens.$oc-semantic-color-text-inverted;
            }
        }

        &__surplus {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: tokens.$oc-base-dimension-16;
            min-height: tokens.$oc-base-dimension-16;
            color: tokens.$oc-semantic-color-text-secondary;
            text-decoration: none;
        }
    }
</style>
